import type { PageContext, PageId } from './types';
import { PAGE_LABELS as PAGE_LABELS_MAP } from './types';
import type { GenConfig, OrganizationType } from '../../types';

export interface PageContextInput {
  activeTab: string;
  genStep: number;
  genConfig: GenConfig;
  organizationName: string;
  organizationType: OrganizationType;
  name: string;
  title: string | null;
}

interface ContextCacheEntry {
  [key: string]: unknown;
}

// Module-level cache：接收子頁面廣播的細節狀態
const contextCache: Record<string, ContextCacheEntry> = {};

if (typeof window !== 'undefined') {
  window.addEventListener('assistant:context-update', (e: Event) => {
    const event = e as CustomEvent<{ pageId: string; data: ContextCacheEntry }>;
    contextCache[event.detail.pageId] = {
      ...contextCache[event.detail.pageId],
      ...event.detail.data,
    };
  });
}

export function collectPageContext(input: PageContextInput): PageContext {
  const pageId: PageId = (input.activeTab as PageId) in PAGE_LABELS_MAP
    ? (input.activeTab as PageId)
    : 'toolkit';

  const pageLabel = PAGE_LABELS_MAP[pageId] ?? input.activeTab;

  const context: PageContext = {
    pageId,
    pageLabel,
    globalState: {
      organizationName: input.organizationName,
      organizationType: input.organizationType,
      name: input.name,
      title: input.title,
    },
  };

  // toolkit 頁面可能正在跑 QuickGen 流程，帶入快速生成狀態
  if (pageId === 'toolkit') {
    // 只保留可序列化欄位（string / boolean），排除 File[] 等 image-type answers
    const textAnswers: Record<string, string | boolean> = {};
    for (const [id, val] of Object.entries(input.genConfig.answers)) {
      if (typeof val === 'string' || typeof val === 'boolean') textAnswers[id] = val;
    }
    context.quickGen = {
      step: input.genStep,
      // 舊欄位（保留給舊 pipeline / 既有組件）
      ratio: input.genConfig.answers['imageAspectRatio'] as string,
      style: input.genConfig.answers['imageStyle'] as string,
      tone: input.genConfig.answers['textToneInImage'] as string,
      content: input.genConfig.answers['contentInImage'] as string,
      // 新欄位（給 agentic 工具用，支援自訂題目 id 的 toolkit）
      flowModelId: input.genConfig.flowModelId,
      answers: textAnswers,
    };
  }

  if (pageId === 'settings') {
    context.settings = {
      organizationName: input.organizationName,
      organizationType: input.organizationType,
      name: input.name,
      title: input.title,
    };
  }

  return context;
}
